// src/components/KanbanBoard.js
import React, { useState } from 'react';
import KanbanColumn from './KanbanColumn';
import FiltrosBar from './FiltrosBar';
import FaturamentoModal from './FaturamentoModal';
import ConclusaoModal from './ConclusaoModal';
import AgendamentoEnvioModal from './AgendamentoEnvioModal';
import { oportunidadesAPI } from '../services/api';

const API_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001/api' 
  : '/api';

const COLUNAS = [
  { id: 'solicitacao_orcamento', titulo: 'Solicitação de Orçamento', cor: 'gray' },
  { id: 'orcamento_enviado', titulo: 'Orçamento Enviado', cor: 'yellow' },
  { id: 'aguardando_atendimento', titulo: 'Aguardando Atendimento', cor: 'green' },
  { id: 'em_andamento', titulo: 'Em Andamento', cor: 'blue' },
  { id: 'finalizada', titulo: 'Finalizada', cor: 'purple' },
  { id: 'faturar', titulo: 'Faturar', cor: 'orange' },
  { id: 'faturado', titulo: 'Faturado', cor: 'indigo' }
];

function KanbanBoard({ oportunidades, onAtualizarStatus, onRecarregar, filtros, onFiltrar }) {
  const [draggedCard, setDraggedCard] = useState(null);
  const [modalFaturamentoAberto, setModalFaturamentoAberto] = useState(false);
  const [oportunidadeFaturamento, setOportunidadeFaturamento] = useState(null);
  const [modalConclusaoAberto, setModalConclusaoAberto] = useState(false);
  const [oportunidadeConclusao, setOportunidadeConclusao] = useState(null);
  const [modalAgendamentoAberto, setModalAgendamentoAberto] = useState(false);
  const [oportunidadeAgendamento, setOportunidadeAgendamento] = useState(null);
  const scrollRef = React.useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const getOportunidadesPorStatus = (status) => {
    const ops = oportunidades.filter(opp => opp.status === status);
    return ops.sort((a, b) => {
      const getNumero = (titulo) => {
        const match = titulo.match(/OS (\d+)/);
        return match ? parseInt(match[1]) : 0;
      };
      return getNumero(b.titulo) - getNumero(a.titulo);
    });
  };

  const handleDragStart = (e, oportunidade) => {
    setDraggedCard(oportunidade);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, novoStatus) => {
    e.preventDefault();

    if (!draggedCard) return;

    const statusAntigo = draggedCard.status;

    if (novoStatus === 'faturar' && statusAntigo !== 'faturar') {
      setOportunidadeFaturamento(draggedCard);
      setModalFaturamentoAberto(true);
      setDraggedCard(null);
      return;
    }

    if (novoStatus === 'finalizada' && statusAntigo === 'em_andamento') {
      setOportunidadeConclusao(draggedCard);
      setModalConclusaoAberto(true);
      setDraggedCard(null);
      return;
    }

    if (statusAntigo === 'faturar' && novoStatus !== 'faturar') {
      try {
        await fetch(`${API_URL}/lembretes-faturamento/${draggedCard.id}`, {
          method: 'DELETE'
        });
        console.log('✅ Lembrete de faturamento desativado');
      } catch (error) {
        console.error('Erro ao desativar lembrete:', error);
      }
    }

    if (draggedCard.status !== novoStatus) {
      const confirmar = window.confirm(
        `Mover "${draggedCard.titulo}" para "${COLUNAS.find(c => c.id === novoStatus)?.titulo}"?\n\n` +
        `Isso atualizará o status no sistema.`
      );

      if (confirmar) {
        await onAtualizarStatus(draggedCard.id, novoStatus);
      }
    }

    setDraggedCard(null);
  };

  const handleConfirmarFaturamento = async (observacoes) => {
    try {
      await onAtualizarStatus(oportunidadeFaturamento.id, 'faturar');

      const response = await fetch(`${API_URL}/lembretes-faturamento`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oportunidade_id: oportunidadeFaturamento.id,
          observacoes: observacoes
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ OS enviada para faturamento!\n📱 Diretoria notificada via WhatsApp!');
      } else {
        alert('⚠️ OS movida mas não foi possível notificar a diretoria.');
      }

      await onRecarregar();

    } catch (error) {
      console.error('Erro ao processar faturamento:', error);
      alert('Erro ao processar faturamento!');
    }
  };

  const handleConfirmarConclusao = async (dadosConclusao) => {
    try {
      console.log('📤 Enviando conclusão:', dadosConclusao);

      // 1. Atualiza status para "finalizada"
      await onAtualizarStatus(oportunidadeConclusao.id, 'finalizada');

      // 2. Envia notificação WhatsApp diretamente com dados do formulário
      const response = await fetch(`${API_URL}/tecnicos/notificar-conclusao-cliente`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oportunidade_id: oportunidadeConclusao.id,
          nome_cliente: dadosConclusao.nome_cliente,
          telefone: dadosConclusao.telefone,
          tecnico_id: dadosConclusao.tecnico_id,
          valor: dadosConclusao.valor,
          tipo_servico: dadosConclusao.tipo_servico
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Serviço finalizado!\n📱 Cliente notificado via WhatsApp!');
      } else {
        alert('⚠️ OS finalizada mas não foi possível notificar o cliente.\n' + (data.error || ''));
      }

      setModalConclusaoAberto(false);
      setOportunidadeConclusao(null);

      await onRecarregar();

    } catch (error) {
      console.error('Erro ao processar conclusão:', error);
      alert('Erro ao processar conclusão: ' + error.message);
      throw error;
    }
  };

  const handleDeletar = async (id) => {
    try {
      console.log('🗑️ Deletando oportunidade ID:', id);
      await oportunidadesAPI.deletar(id);
      console.log('✅ Oportunidade deletada com sucesso!');
      alert('OS deletada com sucesso!');
      onRecarregar();
    } catch (error) {
      console.error('❌ Erro ao deletar:', error);
      alert('Erro ao deletar OS: ' + error.message);
    }
  };

  const handleReprovar = async (id, motivo) => {
    try {
      console.log('❌ Reprovando orçamento ID:', id);
      console.log('📝 Motivo:', motivo);

      await oportunidadesAPI.reprovar(id, motivo);

      console.log('✅ Orçamento reprovado com sucesso!');
      alert('✅ Orçamento reprovado!\n\nA OS foi removida do CRM e marcada como "Reprovado" no Gestão Click.');
      onRecarregar();
    } catch (error) {
      console.error('❌ Erro ao reprovar:', error);
      alert('Erro ao reprovar orçamento: ' + error.message);
    }
  };

  const handleRegistrarFollowUp = async (id, observacao) => {
    try {
      console.log('📞 Registrando follow-up ID:', id);
      console.log('📝 Observação:', observacao);

      const response = await fetch(`${API_URL}/oportunidades/${id}/followup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ observacao })
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Follow-up registrado!');
        alert(`✅ Contato registrado com sucesso!\n\n${data.message}\n\nSalvo nas observações internas do Gestão Click.`);
        onRecarregar();
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (error) {
      console.error('❌ Erro ao registrar follow-up:', error);
      alert('Erro ao registrar contato: ' + error.message);
    }
  };

  const handleAgendarEnvio = async (oportunidade) => {
    setOportunidadeAgendamento(oportunidade);
    setModalAgendamentoAberto(true);
  };

  const handleConfirmarAgendamento = async (dadosAgendamento) => {
    try {
      console.log('⏰ Agendando envio:', dadosAgendamento);

      const response = await fetch(`${API_URL}/oportunidades/${oportunidadeAgendamento.id}/agendar-envio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dadosAgendamento)
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Envio agendado com sucesso!');
        alert(`✅ Envio agendado com sucesso!\n\nO orçamento será enviado automaticamente no horário escolhido.`);
        setModalAgendamentoAberto(false);
        setOportunidadeAgendamento(null);
        onRecarregar();
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (error) {
      console.error('❌ Erro ao agendar envio:', error);
      alert('Erro ao agendar envio: ' + error.message);
    }
  };

  React.useEffect(() => {
    window.onReprovar = handleReprovar;
    window.onRegistrarFollowUp = handleRegistrarFollowUp;
    window.onAgendarEnvio = handleAgendarEnvio;
    return () => {
      delete window.onReprovar;
      delete window.onRegistrarFollowUp;
      delete window.onAgendarEnvio;
    };
  }, []);

  return (
    <div className="kanban-container">
      <FiltrosBar filtros={filtros} onFiltrar={onFiltrar} />

      <div className="kanban-nav-controls">
        <button onClick={scrollLeft} className="nav-btn nav-btn-left" title="Rolar para esquerda">
          ← Anterior
        </button>
        <div className="nav-info">
          Role horizontalmente ou use as setas
        </div>
        <button onClick={scrollRight} className="nav-btn nav-btn-right" title="Rolar para direita">
          Próxima →
        </button>
      </div>

      <div className="kanban-scroll" ref={scrollRef}>
        {COLUNAS.map(coluna => (
          <KanbanColumn
            key={coluna.id}
            coluna={coluna}
            oportunidades={getOportunidadesPorStatus(coluna.id)}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onRecarregar={onRecarregar}
            onDeletar={handleDeletar}
          />
        ))}
      </div>

      {modalFaturamentoAberto && (
        <FaturamentoModal
          isOpen={modalFaturamentoAberto}
          onClose={() => {
            setModalFaturamentoAberto(false);
            setOportunidadeFaturamento(null);
          }}
          oportunidade={oportunidadeFaturamento}
          onConfirmar={handleConfirmarFaturamento}
        />
      )}

      {modalConclusaoAberto && (
        <ConclusaoModal
          isOpen={modalConclusaoAberto}
          onClose={() => {
            setModalConclusaoAberto(false);
            setOportunidadeConclusao(null);
          }}
          oportunidade={oportunidadeConclusao}
          onConfirmar={handleConfirmarConclusao}
        />
      )}

      {modalAgendamentoAberto && (
        <AgendamentoEnvioModal
          isOpen={modalAgendamentoAberto}
          onClose={() => {
            setModalAgendamentoAberto(false);
            setOportunidadeAgendamento(null);
          }}
          oportunidade={oportunidadeAgendamento}
          onConfirmar={handleConfirmarAgendamento}
        />
      )}
    </div>
  );
}

export default KanbanBoard;